import { v4 as uuidV4 } from 'uuid';
import { HubOption, StateCondition, SubCondition } from 'dorian-shared/types/branch/HubOption';
import type { ReplayCondition, TextDestCondition, IconCondition, TagCondition, BorderCondition } from 'dorian-shared/types/branch/HubOption';

/**
 * Deep-clone a HubOption, preserving all nested stateConditions/subConditions.
 * Use for clipboard copy where UUIDs should stay the same.
 */
export function deepCloneOption(option: HubOption): HubOption {
  return {
    ...option,
    replay: option.replay ? { ...option.replay } : null,
    score: option.score ? { ...option.score } : null,
    stateConditions: option.stateConditions?.map((cond) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
    replayConditions: option.replayConditions?.map((cond: ReplayCondition) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
    textDestConditions: option.textDestConditions?.map((cond: TextDestCondition) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
    iconConditions: option.iconConditions?.map((cond: IconCondition) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
    tagTopConditions: option.tagTopConditions?.map((cond: TagCondition) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
    tagBottomConditions: option.tagBottomConditions?.map((cond: TagCondition) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
    borderConditions: option.borderConditions?.map((cond: BorderCondition) => ({
      ...cond,
      subConditions: cond.subConditions.map((sub: SubCondition) => ({ ...sub })),
    })),
  };
}

/**
 * Deep-clone a HubOption with fresh UUIDs at every level.
 * Use for paste/duplicate where identity must be unique.
 */
export function cloneWithNewUuids(option: HubOption): HubOption {
  return {
    ...option,
    id: undefined,
    uuid: uuidV4(),
    replay: option.replay ? { ...option.replay } : null,
    score: option.score ? {
      ...option.score,
      uuid: uuidV4(),
      numeratorUuid: uuidV4(),
      denominatorUuid: uuidV4(),
    } : null,
    stateConditions: option.stateConditions?.map((cond: StateCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
    replayConditions: option.replayConditions?.map((cond: ReplayCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
    textDestConditions: option.textDestConditions?.map((cond: TextDestCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
    iconConditions: option.iconConditions?.map((cond: IconCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
    tagTopConditions: option.tagTopConditions?.map((cond: TagCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
    tagBottomConditions: option.tagBottomConditions?.map((cond: TagCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
    borderConditions: option.borderConditions?.map((cond: BorderCondition) => ({
      ...cond,
      uuid: uuidV4(),
      subConditions: cond.subConditions.map((sub: SubCondition) => ({
        ...sub,
        uuid: uuidV4(),
      })),
    })),
  };
}
